/**
 * NotificacoesFilters — Filter bar.
 *
 * Spec §3.2: status (unread/read/all/dismissed) + sources multi + severity multi + q + date range.
 * Pattern espelha audit/FiltersToolbar.tsx (sem importar — usar Mantine inline).
 */
import { Group, MultiSelect, SegmentedControl, TextInput, Button, Box } from '@mantine/core'
import { IconSearch, IconX } from '@tabler/icons-react'
import type { FeedFilters, FeedStatus, Severity } from '@/api/notifications-feed'

/**
 * Sources visiveis no MultiSelect: 15 V1 + 10 V2 (TASK-2594).
 *
 * V2 sources chegam via /ingest/* endpoints (TASK-2582+):
 *  cloudtrail, aws_health, aws_backup, cost_anomaly, alb_target_health,
 *  ecs_service, gmud_lifecycle, provisioning_saga, cicd_gitlab, security_auth.
 */
const SOURCE_OPTIONS = [
  // V1 (14) — 'email' removido (era engano: email eh channel)
  'audit', 'notification', 'inbox', 'chamado',
  'cloudwatch_alarm', 'guardduty', 'securityhub',
  'zabbix', 'ztna', 'provisioning', 'database',
  'rbac', 'finops', 'gitlab',
  // V2 (10) — TASK-2594
  'cloudtrail', 'aws_health', 'aws_backup', 'cost_anomaly', 'alb_target_health',
  'ecs_service', 'gmud_lifecycle', 'provisioning_saga', 'cicd_gitlab', 'security_auth',
] as const

const SEVERITY_OPTIONS: { label: string; value: Severity }[] = [
  { label: 'Debug', value: 'debug' },
  { label: 'Info', value: 'info' },
  { label: 'Warning', value: 'warning' },
  { label: 'Critical', value: 'critical' },
  { label: 'Fatal', value: 'fatal' },
]

const STATUS_OPTIONS: { label: string; value: FeedStatus }[] = [
  { label: 'Não lidas', value: 'unread' },
  { label: 'Lidas', value: 'read' },
  { label: 'Todas', value: 'all' },
  { label: 'Descartadas', value: 'dismissed' },
]

interface Props {
  value: FeedFilters
  onChange: (next: FeedFilters) => void
}

export function NotificacoesFilters({ value, onChange }: Props) {
  const hasActive =
    (value.sources && value.sources.length > 0) ||
    (value.severity && value.severity.length > 0) ||
    !!value.q ||
    !!value.date_from ||
    !!value.date_to

  return (
    <Box>
      {/* MVP mobile: overflow scroll. Full responsive em F5. */}
      <Group gap="sm" wrap="nowrap" style={{ overflowX: 'auto', flexWrap: 'nowrap' }}>
        <SegmentedControl
          size="xs"
          data={STATUS_OPTIONS}
          value={value.status ?? 'unread'}
          onChange={(v) => onChange({ ...value, status: v as FeedStatus })}
        />

        <MultiSelect
          size="xs"
          placeholder="Origem"
          data={SOURCE_OPTIONS.map((s) => ({ value: s, label: s }))}
          value={value.sources ?? []}
          onChange={(v) => onChange({ ...value, sources: v })}
          searchable
          clearable
          maw={260}
        />

        <MultiSelect
          size="xs"
          placeholder="Severidade"
          data={SEVERITY_OPTIONS}
          value={value.severity ?? []}
          onChange={(v) => onChange({ ...value, severity: v as Severity[] })}
          clearable
          maw={220}
        />

        <TextInput
          size="xs"
          placeholder="Buscar título/mensagem..."
          leftSection={<IconSearch size={14} />}
          value={value.q ?? ''}
          onChange={(e) => onChange({ ...value, q: e.currentTarget.value })}
          miw={200}
        />

        {hasActive && (
          <Button
            size="xs"
            variant="subtle"
            leftSection={<IconX size={14} />}
            onClick={() => onChange({ status: value.status })}
          >
            Limpar filtros
          </Button>
        )}
      </Group>
    </Box>
  )
}
